import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronDown, Zap, Target, MessageCircle, Image as ImageIcon,
  FileImage, Minimize2, Maximize2, Crop, Move, FileText, FileDown,
  ScanText, Merge, Scissors, X, Percent, Instagram, Facebook, Youtube,
  Twitter, Linkedin, Music, ImageIcon as PinterestIcon, Share2, 
  Edit, PenTool, Unlock, Trash2, ArrowUpDown, Droplets, Globe, FileOutput, Shield, RotateCw, Layers, GitCompare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const compressTools = [
  { name: 'Image Compressor', path: '/', icon: ImageIcon, description: 'Full-featured compression', color: 'text-blue-500', badge: 'Main' },
  { name: 'Quick 50KB', path: '/quick-50kb', icon: Target, description: 'Compress to 50KB', color: 'text-orange-500', badge: '50KB' },
  { name: 'Quick 100KB', path: '/quick-100kb', icon: Target, description: 'Compress to 100KB', color: 'text-green-500', badge: '100KB' },
  { name: 'Quick 200KB', path: '/quick-200kb', icon: Target, description: 'Compress to 200KB', color: 'text-blue-500', badge: '200KB' },
  { name: 'Quick 500KB', path: '/quick-500kb', icon: Target, description: 'Compress to 500KB', color: 'text-purple-500', badge: '500KB' },
  { name: 'Quick 1MB', path: '/quick-1mb', icon: Target, description: 'Compress to 1MB', color: 'text-cyan-500', badge: '1MB' },
  { name: 'WhatsApp', path: '/whatsapp-compress', icon: MessageCircle, description: '500×500px square', color: 'text-emerald-500', badge: '500px' },
];

const resizeTools = [
  { name: 'Resize to A4', path: '/resize-to-a4', icon: FileText, description: 'A4 paper size for printing', color: 'text-amber-500', badge: 'A4' },
  { name: 'Resize to 50KB', path: '/resize-image-to-50kb', icon: Maximize2, description: 'Resize image to 50KB', color: 'text-green-500', badge: '50KB' },
  { name: 'Resize to 100KB', path: '/resize-image-to-100kb', icon: Maximize2, description: 'Resize image to 100KB', color: 'text-green-500', badge: '100KB' },
  { name: 'Resize to 200KB', path: '/resize-image-to-200kb', icon: Maximize2, description: 'Resize image to 200KB', color: 'text-green-500', badge: '200KB' },
  { name: 'Resize to 2MB', path: '/resize-image-to-2mb', icon: Maximize2, description: 'Resize image to 2MB', color: 'text-green-500', badge: '2MB' },
  { name: 'Resize to 300×300', path: '/resize-image-to-300x300', icon: Crop, description: 'Crop, fit, or stretch', color: 'text-purple-500', badge: '300px' },
  { name: 'Resize by Percentage', path: '/resize-by-percentage', icon: Percent, description: '25%, 50%, 75% scaling', color: 'text-indigo-500', badge: '%' },
];

const socialMediaTools = [
  { name: 'Resize for WhatsApp', path: '/resize-image-for-whatsapp', icon: MessageCircle, description: '500×500px for WhatsApp', color: 'text-emerald-500', badge: 'WA' },
  { name: 'Resize for Instagram', path: '/resize-for-instagram', icon: Instagram, description: 'Posts, Stories, Reels', color: 'text-pink-500', badge: 'IG' },
  { name: 'Resize for Facebook', path: '/resize-for-facebook', icon: Facebook, description: 'Posts, Cover, Profile', color: 'text-blue-600', badge: 'FB' },
  { name: 'Resize for YouTube', path: '/resize-for-youtube', icon: Youtube, description: 'Thumbnails, Banners', color: 'text-red-500', badge: 'YT' },
  { name: 'Resize for Twitter/X', path: '/resize-for-twitter', icon: Twitter, description: 'Posts, Headers', color: 'text-slate-800', badge: 'X' },
  { name: 'Resize for LinkedIn', path: '/resize-for-linkedin', icon: Linkedin, description: 'Posts, Banners', color: 'text-blue-700', badge: 'LI' },
  { name: 'Resize for TikTok', path: '/resize-for-tiktok', icon: Music, description: 'Videos, Thumbnails', color: 'text-pink-600', badge: 'TT' },
  { name: 'Resize for Pinterest', path: '/resize-for-pinterest', icon: PinterestIcon, description: 'Pins, Board Covers', color: 'text-red-600', badge: 'Pin' },
];

const convertTools = [
  { name: 'JPG to PNG', path: '/convert-jpg-to-png', icon: Move, description: 'Convert JPG to PNG', color: 'text-orange-500', badge: 'PNG' },
  { name: 'JPG to WebP', path: '/convert-jpg-to-webp', icon: Move, description: 'Convert JPG to WebP', color: 'text-orange-500', badge: 'WebP' },
  { name: 'PNG to JPG', path: '/convert-png-to-jpg', icon: Move, description: 'Convert PNG to JPG', color: 'text-blue-500', badge: 'JPG' },
  { name: 'PNG to WebP', path: '/convert-png-to-webp', icon: Move, description: 'Convert PNG to WebP', color: 'text-blue-500', badge: 'WebP' },
  { name: 'PNG to SVG', path: '/convert-png-to-svg', icon: Move, description: 'Vectorize to SVG', color: 'text-violet-500', badge: 'SVG' },
  { name: 'WebP to PNG', path: '/convert-webp-to-png', icon: Move, description: 'Convert WebP to PNG', color: 'text-green-500', badge: 'PNG' },
  { name: 'WebP to JPG', path: '/convert-webp-to-jpg', icon: Move, description: 'Convert WebP to JPG', color: 'text-green-500', badge: 'JPG' },
  { name: 'HEIC to JPG', path: '/convert-heic-to-jpg', icon: Move, description: 'iPhone HEIC to JPG', color: 'text-rose-500', badge: 'JPG' },
  { name: 'HEIC to PNG', path: '/convert-heic-to-png', icon: Move, description: 'iPhone HEIC to PNG', color: 'text-rose-500', badge: 'PNG' },
  { name: 'SVG to PNG', path: '/convert-svg-to-png', icon: Move, description: 'Convert SVG to PNG', color: 'text-emerald-500', badge: 'PNG' },
  { name: 'SVG to JPG', path: '/convert-svg-to-jpg', icon: Move, description: 'Convert SVG to JPG', color: 'text-emerald-500', badge: 'JPG' },
];

const moreConvertTools = {
  jpg: {
    label: 'From JPG/JPEG',
    color: 'bg-orange-500',
    textColor: 'text-orange-500',
    tools: [
      { name: 'JPG to PNG', path: '/convert-jpg-to-png', target: 'PNG' },
      { name: 'JPG to WebP', path: '/convert-jpg-to-webp', target: 'WebP' },
      { name: 'JPG to JPEG', path: '/convert-jpg-to-jpeg', target: 'JPEG' },
      { name: 'JPG to BMP', path: '/convert-jpg-to-bmp', target: 'BMP' },
      { name: 'JPG to TIFF', path: '/convert-jpg-to-tiff', target: 'TIFF' },
      { name: 'JPG to ICO', path: '/convert-jpg-to-ico', target: 'ICO' },
      { name: 'JPG to SVG', path: '/convert-jpg-to-svg', target: 'SVG' },
      { name: 'JPG to HEIC', path: '/convert-jpg-to-heic', target: 'HEIC' },
    ]
  },
  png: {
    label: 'From PNG',
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    tools: [
      { name: 'PNG to JPG', path: '/convert-png-to-jpg', target: 'JPG' },
      { name: 'PNG to WebP', path: '/convert-png-to-webp', target: 'WebP' },
      { name: 'PNG to SVG', path: '/convert-png-to-svg', target: 'SVG' },
      { name: 'PNG to JPEG', path: '/convert-png-to-jpeg', target: 'JPEG' },
      { name: 'PNG to BMP', path: '/convert-png-to-bmp', target: 'BMP' },
      { name: 'PNG to TIFF', path: '/convert-png-to-tiff', target: 'TIFF' },
      { name: 'PNG to ICO', path: '/convert-png-to-ico', target: 'ICO' },
      { name: 'PNG to HEIC', path: '/convert-png-to-heic', target: 'HEIC' },
    ]
  },
  webp: {
    label: 'From WebP',
    color: 'bg-green-500',
    textColor: 'text-green-500',
    tools: [
      { name: 'WebP to PNG', path: '/convert-webp-to-png', target: 'PNG' },
      { name: 'WebP to JPG', path: '/convert-webp-to-jpg', target: 'JPG' },
      { name: 'WebP to JPEG', path: '/convert-webp-to-jpeg', target: 'JPEG' },
      { name: 'WebP to BMP', path: '/convert-webp-to-bmp', target: 'BMP' },
      { name: 'WebP to TIFF', path: '/convert-webp-to-tiff', target: 'TIFF' },
      { name: 'WebP to ICO', path: '/convert-webp-to-ico', target: 'ICO' },
      { name: 'WebP to SVG', path: '/convert-webp-to-svg', target: 'SVG' },
      { name: 'WebP to HEIC', path: '/convert-webp-to-heic', target: 'HEIC' },
    ]
  },
  heic: {
    label: 'From HEIC (iPhone)',
    color: 'bg-rose-500',
    textColor: 'text-rose-500',
    tools: [
      { name: 'HEIC to JPG', path: '/convert-heic-to-jpg', target: 'JPG' },
      { name: 'HEIC to JPEG', path: '/convert-heic-to-jpeg', target: 'JPEG' },
      { name: 'HEIC to PNG', path: '/convert-heic-to-png', target: 'PNG' },
      { name: 'HEIC to WebP', path: '/convert-heic-to-webp', target: 'WebP' },
      { name: 'HEIC to BMP', path: '/convert-heic-to-bmp', target: 'BMP' },
      { name: 'HEIC to TIFF', path: '/convert-heic-to-tiff', target: 'TIFF' },
      { name: 'HEIC to ICO', path: '/convert-heic-to-ico', target: 'ICO' },
      { name: 'HEIC to SVG', path: '/convert-heic-to-svg', target: 'SVG' },
    ]
  },
  svg: {
    label: 'From SVG',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-500',
    tools: [
      { name: 'SVG to PNG', path: '/convert-svg-to-png', target: 'PNG' },
      { name: 'SVG to JPG', path: '/convert-svg-to-jpg', target: 'JPG' },
      { name: 'SVG to JPEG', path: '/convert-svg-to-jpeg', target: 'JPEG' },
      { name: 'SVG to WebP', path: '/convert-svg-to-webp', target: 'WebP' },
      { name: 'SVG to BMP', path: '/convert-svg-to-bmp', target: 'BMP' },
      { name: 'SVG to TIFF', path: '/convert-svg-to-tiff', target: 'TIFF' },
      { name: 'SVG to ICO', path: '/convert-svg-to-ico', target: 'ICO' },
      { name: 'SVG to HEIC', path: '/convert-svg-to-heic', target: 'HEIC' },
    ]
  },
  bmp: {
    label: 'From BMP',
    color: 'bg-indigo-500',
    textColor: 'text-indigo-500',
    tools: [
      { name: 'BMP to PNG', path: '/convert-bmp-to-png', target: 'PNG' },
      { name: 'BMP to JPG', path: '/convert-bmp-to-jpg', target: 'JPG' },
      { name: 'BMP to JPEG', path: '/convert-bmp-to-jpeg', target: 'JPEG' },
      { name: 'BMP to WebP', path: '/convert-bmp-to-webp', target: 'WebP' },
      { name: 'BMP to TIFF', path: '/convert-bmp-to-tiff', target: 'TIFF' },
      { name: 'BMP to ICO', path: '/convert-bmp-to-ico', target: 'ICO' },
      { name: 'BMP to SVG', path: '/convert-bmp-to-svg', target: 'SVG' },
      { name: 'BMP to HEIC', path: '/convert-bmp-to-heic', target: 'HEIC' },
    ]
  },
  tiff: {
    label: 'From TIFF',
    color: 'bg-purple-500',
    textColor: 'text-purple-500',
    tools: [
      { name: 'TIFF to PNG', path: '/convert-tiff-to-png', target: 'PNG' },
      { name: 'TIFF to JPG', path: '/convert-tiff-to-jpg', target: 'JPG' },
      { name: 'TIFF to JPEG', path: '/convert-tiff-to-jpeg', target: 'JPEG' },
      { name: 'TIFF to WebP', path: '/convert-tiff-to-webp', target: 'WebP' },
      { name: 'TIFF to BMP', path: '/convert-tiff-to-bmp', target: 'BMP' },
      { name: 'TIFF to ICO', path: '/convert-tiff-to-ico', target: 'ICO' },
      { name: 'TIFF to SVG', path: '/convert-tiff-to-svg', target: 'SVG' },
      { name: 'TIFF to HEIC', path: '/convert-tiff-to-heic', target: 'HEIC' },
    ]
  },
  ico: {
    label: 'From ICO',
    color: 'bg-cyan-500',
    textColor: 'text-cyan-500',
    tools: [
      { name: 'ICO to PNG', path: '/convert-ico-to-png', target: 'PNG' },
      { name: 'ICO to JPG', path: '/convert-ico-to-jpg', target: 'JPG' },
      { name: 'ICO to JPEG', path: '/convert-ico-to-jpeg', target: 'JPEG' },
      { name: 'ICO to WebP', path: '/convert-ico-to-webp', target: 'WebP' },
      { name: 'ICO to BMP', path: '/convert-ico-to-bmp', target: 'BMP' },
      { name: 'ICO to TIFF', path: '/convert-ico-to-tiff', target: 'TIFF' },
      { name: 'ICO to SVG', path: '/convert-ico-to-svg', target: 'SVG' },
      { name: 'ICO to HEIC', path: '/convert-ico-to-heic', target: 'HEIC' },
    ]
  },
};

// Calculate total count for moreConvertTools
const moreConvertToolsCount = Object.values(moreConvertTools).reduce((sum, group) => sum + group.tools.length, 0);

const documentTools = [
  { name: 'Image to Text (OCR)', path: '/image-to-text', icon: ScanText, description: 'AI-powered text extraction', color: 'text-blue-500', badge: 'AI' },
  { name: 'PDF to Text', path: '/pdf-to-text', icon: FileText, description: 'Extract text from PDF', color: 'text-red-500', badge: 'TXT' },
  { name: 'Text to PDF', path: '/text-to-pdf-new', icon: FileDown, description: 'Convert text to PDF', color: 'text-emerald-500', badge: 'PDF' },
  { name: 'PDF Merge', path: '/pdf-merge', icon: Merge, description: 'Combine multiple PDFs', color: 'text-red-500', badge: 'Merge' },
  { name: 'PDF Split', path: '/pdf-split', icon: Scissors, description: 'Extract pages from PDF', color: 'text-orange-500', badge: 'Split' },
  { name: 'Markdown to PDF', path: '/markdown-to-pdf', icon: FileDown, description: 'Convert MD to PDF', color: 'text-violet-500', badge: 'MD' },
  { name: 'Merge Docs to PDF', path: '/merge-documents-to-pdf', icon: Merge, description: 'Combine any docs to PDF', color: 'text-purple-500', badge: 'New' },
];

const pdfConvertTools = [
  { name: 'Images to PDF', path: '/images-to-pdf', icon: FileDown, description: 'Multiple images to PDF', color: 'text-red-500', badge: 'Multi' },
  { name: 'JPG to PDF', path: '/jpg-to-pdf', icon: FileDown, description: 'Convert JPG to PDF', color: 'text-orange-500', badge: 'PDF' },
  { name: 'PNG to PDF', path: '/png-to-pdf', icon: FileDown, description: 'Convert PNG to PDF', color: 'text-blue-500', badge: 'PDF' },
  { name: 'WebP to PDF', path: '/webp-to-pdf', icon: FileDown, description: 'Convert WebP to PDF', color: 'text-green-500', badge: 'PDF' },
  { name: 'HEIC to PDF', path: '/heic-to-pdf', icon: FileDown, description: 'Convert HEIC to PDF', color: 'text-purple-500', badge: 'PDF' },
  { name: 'SVG to PDF', path: '/svg-to-pdf', icon: FileDown, description: 'Convert SVG to PDF', color: 'text-cyan-500', badge: 'PDF' },
  { name: 'TIFF to PDF', path: '/tiff-to-pdf', icon: FileDown, description: 'Convert TIFF to PDF', color: 'text-amber-500', badge: 'PDF' },
];

const docPdfTools = [
  { name: 'Word to PDF', path: '/word-to-pdf', icon: FileDown, description: 'DOC/DOCX to PDF', color: 'text-blue-600', badge: 'Word' },
  { name: 'PowerPoint to PDF', path: '/powerpoint-to-pdf', icon: FileDown, description: 'PPT/PPTX to PDF', color: 'text-orange-500', badge: 'PPT' },
  { name: 'Excel to PDF', path: '/excel-to-pdf', icon: FileDown, description: 'XLS/XLSX to PDF', color: 'text-green-600', badge: 'Excel' },
  { name: 'DOCX to PDF', path: '/docx-to-pdf', icon: FileDown, description: 'Modern Word format', color: 'text-blue-500', badge: 'DOCX' },
  { name: 'DOC to PDF', path: '/doc-to-pdf', icon: FileDown, description: 'Legacy Word format', color: 'text-blue-400', badge: 'DOC' },
  { name: 'PPTX to PDF', path: '/pptx-to-pdf', icon: FileDown, description: 'Modern PowerPoint', color: 'text-orange-400', badge: 'PPTX' },
  { name: 'PPT to PDF', path: '/ppt-to-pdf', icon: FileDown, description: 'Legacy PowerPoint', color: 'text-orange-300', badge: 'PPT' },
  { name: 'XLSX to PDF', path: '/xlsx-to-pdf', icon: FileDown, description: 'Modern Excel', color: 'text-green-500', badge: 'XLSX' },
  { name: 'XLS to PDF', path: '/xls-to-pdf', icon: FileDown, description: 'Legacy Excel', color: 'text-green-400', badge: 'XLS' },
  { name: 'ODT to PDF', path: '/odt-to-pdf', icon: FileDown, description: 'OpenDocument Text', color: 'text-cyan-500', badge: 'ODT' },
  { name: 'ODP to PDF', path: '/odp-to-pdf', icon: FileDown, description: 'OpenDocument Presentation', color: 'text-cyan-400', badge: 'ODP' },
  { name: 'ODS to PDF', path: '/ods-to-pdf', icon: FileDown, description: 'OpenDocument Spreadsheet', color: 'text-cyan-300', badge: 'ODS' },
  { name: 'ODG to PDF', path: '/odg-to-pdf', icon: FileDown, description: 'OpenDocument Graphics', color: 'text-cyan-600', badge: 'ODG' },
  { name: 'RTF to PDF', path: '/rtf-to-pdf', icon: FileDown, description: 'Rich Text Format', color: 'text-purple-500', badge: 'RTF' },
  { name: 'EPUB to PDF', path: '/epub-to-pdf', icon: FileDown, description: 'E-book to PDF', color: 'text-amber-600', badge: 'EPUB' },
];

const pdfManipulationTools = [
  { name: 'Compress PDF', path: '/compress-pdf', icon: Minimize2, description: 'Reduce PDF file size', color: 'text-red-500', badge: 'Compress' },
  { name: 'PDF to Images', path: '/pdf-to-images', icon: FileOutput, description: 'Extract pages as images', color: 'text-blue-500', badge: 'Images' },
  { name: 'Edit PDF', path: '/edit-pdf', icon: Edit, description: 'Add text to PDF', color: 'text-purple-500', badge: 'Edit' },
  { name: 'Sign PDF', path: '/sign-pdf', icon: PenTool, description: 'Draw signature on PDF', color: 'text-teal-500', badge: 'Sign' },
  { name: 'Protect PDF', path: '/protect-pdf', icon: Shield, description: 'Add password protection', color: 'text-green-500', badge: 'Protect' },
  { name: 'Unlock PDF', path: '/unlock-pdf', icon: Unlock, description: 'Remove password', color: 'text-amber-500', badge: 'Unlock' },
  { name: 'Rotate PDF', path: '/rotate-pdf', icon: RotateCw, description: 'Rotate PDF pages', color: 'text-orange-500', badge: 'Rotate' },
  { name: 'Flatten PDF', path: '/flatten-pdf', icon: Layers, description: 'Make PDF non-editable', color: 'text-slate-500', badge: 'Flatten' },
  { name: 'Compare PDFs', path: '/compare-pdf', icon: GitCompare, description: 'Compare two PDFs', color: 'text-blue-600', badge: 'Compare' },
  { name: 'Remove Pages', path: '/remove-pdf-pages', icon: Trash2, description: 'Delete PDF pages', color: 'text-rose-500', badge: 'Delete' },
  { name: 'Extract Pages', path: '/extract-pdf-pages', icon: FileOutput, description: 'Get specific pages', color: 'text-indigo-500', badge: 'Extract' },
  { name: 'Rearrange Pages', path: '/rearrange-pdf-pages', icon: ArrowUpDown, description: 'Reorder PDF pages', color: 'text-violet-500', badge: 'Reorder' },
  { name: 'Watermark PDF', path: '/watermark-pdf', icon: Droplets, description: 'Add watermark to PDF', color: 'text-cyan-500', badge: 'Watermark' },
  { name: 'Webpage to PDF', path: '/webpage-to-pdf', icon: Globe, description: 'Convert URL to PDF', color: 'text-emerald-500', badge: 'Web' },
];

const pdfExportTools = [
  { name: 'PDF to Word', path: '/pdf-to-docx', icon: FileOutput, description: 'Convert PDF to DOCX', color: 'text-blue-600', badge: 'Word' },
  { name: 'PDF to PowerPoint', path: '/pdf-to-pptx', icon: FileOutput, description: 'Convert PDF to PPTX', color: 'text-orange-500', badge: 'PPT' },
  { name: 'PDF to Excel', path: '/pdf-to-xlsx', icon: FileOutput, description: 'Convert PDF to XLSX', color: 'text-green-600', badge: 'Excel' },
  { name: 'PDF to Text', path: '/pdf-to-txt', icon: FileText, description: 'Extract plain text', color: 'text-slate-500', badge: 'TXT' },
  { name: 'PDF to RTF', path: '/pdf-to-rtf', icon: FileOutput, description: 'Convert to Rich Text', color: 'text-purple-500', badge: 'RTF' },
  { name: 'PDF to HTML', path: '/pdf-to-html', icon: FileOutput, description: 'Convert to web page', color: 'text-red-500', badge: 'HTML' },
  { name: 'PDF to ODT', path: '/pdf-to-odt', icon: FileOutput, description: 'OpenDocument format', color: 'text-cyan-500', badge: 'ODT' },
  { name: 'PDF to EPUB', path: '/pdf-to-epub', icon: FileOutput, description: 'Convert to e-book', color: 'text-amber-600', badge: 'EPUB' },
  { name: 'PDF to PNG', path: '/pdf-to-png', icon: FileImage, description: 'High-quality images', color: 'text-emerald-500', badge: 'PNG' },
  { name: 'PDF to JPG', path: '/pdf-to-jpg', icon: FileImage, description: 'Compressed images', color: 'text-rose-500', badge: 'JPG' },
  { name: 'PDF to SVG', path: '/pdf-to-svg', icon: FileImage, description: 'Vector graphics', color: 'text-fuchsia-500', badge: 'SVG' },
  { name: 'PDF to Secure PDF', path: '/pdf-to-secure', icon: Shield, description: 'Add password', color: 'text-teal-500', badge: 'Secure' },
  { name: 'PDF to PDF/A', path: '/pdf-to-pdfa', icon: FileOutput, description: 'Archival format', color: 'text-indigo-500', badge: 'PDF/A' },
];

// Flatten moreConvertTools object into array for allTools
const moreConvertToolsFlat = Object.values(moreConvertTools).flatMap(group => group.tools);

const allTools = [...compressTools, ...resizeTools, ...socialMediaTools, ...convertTools, ...moreConvertToolsFlat, ...documentTools, ...pdfConvertTools, ...docPdfTools, ...pdfManipulationTools, ...pdfExportTools];

const categories = [
  { id: 'compress', name: 'Compress', icon: Minimize2, color: 'bg-blue-500', tools: compressTools },
  { id: 'resize', name: 'Resize', icon: Maximize2, color: 'bg-green-500', tools: resizeTools },
  { id: 'social', name: 'Social Media', icon: Share2, color: 'bg-pink-500', tools: socialMediaTools },
  { id: 'convert', name: 'Convert', icon: Move, color: 'bg-orange-500', tools: convertTools, moreTools: moreConvertTools },
  { id: 'pdftools', name: 'PDF Tools', icon: FileOutput, color: 'bg-red-600', tools: pdfManipulationTools },
  { id: 'pdfexport', name: 'PDF to X', icon: FileOutput, color: 'bg-violet-600', tools: pdfExportTools },
  { id: 'pdf', name: 'Image to PDF', icon: FileDown, color: 'bg-red-500', tools: pdfConvertTools },
  { id: 'docpdf', name: 'Doc to PDF', icon: FileText, color: 'bg-blue-600', tools: docPdfTools },
  { id: 'documents', name: 'Documents', icon: FileText, color: 'bg-purple-500', tools: documentTools },
];

export default function ToolsNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('compress');
  const [showMoreConvert, setShowMoreConvert] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const currentCategory = categories.find(c => c.id === activeCategory);

  return (
    <>
      {/* Trigger Button */}
      <Button 
        ref={buttonRef}
        variant="outline" 
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2 border-2 border-blue-200 dark:border-blue-800 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 hover:border-blue-400 transition-all"
        data-testid="tools-nav-trigger"
      >
        <Zap className="w-5 h-5 text-blue-500" />
        <span className="font-semibold text-slate-700 dark:text-slate-200">Tools</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mega Menu */}
      {isOpen && (
        <div 
          ref={menuRef}
          className="fixed left-1/2 -translate-x-1/2 top-16 w-[95vw] max-w-5xl max-h-[80vh] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">All Tools</h3>
                <p className="text-xs text-slate-500">{allTools.length}+ free tools</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="rounded-full">
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex flex-col md:flex-row h-[calc(80vh-80px)]">
            {/* Categories Sidebar */}
            <div className="md:w-56 flex-shrink-0 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 overflow-x-auto md:overflow-y-auto">
              <div className="flex md:flex-col p-2 gap-1">
                {categories.map((category) => {
                  const Icon = category.icon;
                  const isActive = activeCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => { setActiveCategory(category.id); setShowMoreConvert(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl whitespace-nowrap transition-all ${
                        isActive 
                          ? 'bg-white dark:bg-slate-800 shadow-md text-slate-900 dark:text-white' 
                          : 'hover:bg-white/50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${category.color} flex items-center justify-center`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-left hidden md:block">
                        <p className="font-medium text-sm">{category.name}</p>
                        <p className="text-xs text-slate-500">{category.tools.length} tools</p>
                      </div>
                      <span className="md:hidden text-sm font-medium">{category.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tools Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  {currentCategory && (
                    <>
                      <currentCategory.icon className="w-5 h-5" />
                      {currentCategory.name} Tools
                    </>
                  )}
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {currentCategory?.tools.map((tool) => {
                  const Icon = tool.icon;
                  const isActive = location.pathname === tool.path;
                  
                  return (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all hover:shadow-md ${
                        isActive 
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' 
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${tool.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium text-sm truncate ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-900 dark:text-white'}`}>
                          {tool.name}
                        </p>
                        {tool.description && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{tool.description}</p>
                        )}
                      </div>
                      {tool.badge && (
                        <Badge variant="secondary" className="text-[10px] px-2 py-0.5 bg-slate-100 dark:bg-slate-700">
                          {tool.badge}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* More Convert Tools - Grouped by Source Format */}
              {activeCategory === 'convert' && (
                <div className="mt-6">
                  <button 
                    onClick={() => setShowMoreConvert(!showMoreConvert)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                  >
                    {showMoreConvert ? 'Show Less Formats' : `Show ${moreConvertToolsCount} More Formats`}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showMoreConvert ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showMoreConvert && (
                    <div className="mt-4 space-y-3">
                      {Object.entries(moreConvertTools).map(([key, group]) => (
                        <div key={key} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                          {/* Group Header */}
                          <div className={`flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50`}>
                            <div className={`w-6 h-6 rounded-md ${group.color} flex items-center justify-center`}>
                              <Move className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className={`font-semibold text-sm ${group.textColor}`}>{group.label}</span>
                            <span className="text-xs text-slate-400 ml-auto">{group.tools.length} formats</span>
                          </div>
                          {/* Target Formats */}
                          <div className="flex flex-wrap gap-2 p-3 bg-white dark:bg-slate-800">
                            {group.tools.map((tool) => {
                              const isActive = location.pathname === tool.path;
                              return (
                                <Link
                                  key={tool.path}
                                  to={tool.path}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                    isActive 
                                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 ring-2 ring-blue-400' 
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                                  }`}
                                >
                                  <span className="text-slate-400 dark:text-slate-500">→</span>
                                  <span>{tool.target}</span>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
